import React from 'react';
import SEO from '../components/SEO';
import UtilityPageLayout from '../components/UtilityPageLayout';
import UtilitySection from '../components/UtilitySection';
import { siteInfo } from '../data/siteInfo';

const COOKIE_CATEGORIES = [
  {
    category: 'Essential Cookies',
    badge: 'Always Active',
    badgeMod: 'essential',
    desc: 'These cookies are necessary for the website to function and cannot be switched off. They include cookies that enable you to log in to secure areas, use forms or complete transactions.',
    cookies: [
      { name: 'session_id', purpose: 'Session management', duration: 'Session' },
      { name: 'csrf_token', purpose: 'Security/CSRF protection', duration: 'Session' }
    ]
  },
  {
    category: 'Analytics Cookies',
    badge: 'Requires Consent',
    badgeMod: 'analytics',
    desc: 'These cookies allow us to measure and improve the performance of our website by collecting information about how visitors use it.',
    cookies: [
      { name: '_ga', purpose: 'Google Analytics — distinguishes users', duration: '2 years' },
      { name: '_gid', purpose: 'Google Analytics — distinguishes users (short-term)', duration: '24 hours' },
      { name: '_ga_XXXXXXX', purpose: 'Google Analytics — session state', duration: '2 years' }
    ]
  },
  {
    category: 'Marketing Cookies',
    badge: 'Requires Consent',
    badgeMod: 'marketing',
    desc: 'These cookies may be set by our advertising partners to build a profile of your interests and show relevant ads on other sites.',
    cookies: [
      { name: 'fr', purpose: 'Facebook Pixel — ad tracking', duration: '90 days' },
      { name: 'IDE', purpose: 'Google Ads — ad targeting', duration: '1 year' }
    ]
  },
  {
    category: 'Preference Cookies',
    badge: 'Requires Consent',
    badgeMod: 'preference',
    desc: 'These cookies enable the website to remember information that changes how it behaves or looks, such as your preferred language or region.',
    cookies: [
      { name: 'cookie_consent', purpose: 'Stores your cookie preferences', duration: '1 year' }
    ]
  }
];

const CookiePolicy = () => {
  const companyName = siteInfo.companyName;
  return (
    <>
      <SEO
        title={`Cookie Policy | ${companyName}`}
        description={`${companyName} cookie policy — what cookies we use, why, and how to manage them.`}
      />

    <UtilityPageLayout
      eyebrow="Legal"
      title="Cookie Policy"
      meta={`Last updated: ${siteInfo.legal.legalUpdated}`}
    >
      <UtilitySection title="What Are Cookies?">
        <p>Cookies are small text files placed on your device when you visit a website. They help websites remember your preferences, analyse traffic, and improve the user experience.</p>
      </UtilitySection>

      <section className="utility-section">
        <h2 className="utility-section__title">Categories of Cookies We Use</h2>
        <div className="utility-prose">
          {COOKIE_CATEGORIES.map((cat) => (
            <article key={cat.category} className="utility-cookie-card">
              <header className="utility-cookie-card__head">
                <h3 className="utility-cookie-card__title">{cat.category}</h3>
                <span className={`utility-cookie-badge utility-cookie-badge--${cat.badgeMod}`}>
                  {cat.badge}
                </span>
              </header>
              <div className="utility-cookie-card__body">
                <p>{cat.desc}</p>
                <div className="utility-table-wrap">
                  <table className="utility-table">
                    <thead>
                      <tr>
                        <th scope="col">Cookie Name</th>
                        <th scope="col">Purpose</th>
                        <th scope="col">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cat.cookies.map((c) => (
                        <tr key={c.name}>
                          <td>
                            <code className="utility-code">{c.name}</code>
                          </td>
                          <td>{c.purpose}</td>
                          <td className="utility-table__muted">{c.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <UtilitySection title="How to Manage Cookies">
        <p>You can manage your cookie preferences using our cookie banner when you first visit the site, or at any time by clearing your browser cookies and revisiting.</p>
        <p>You can also manage cookies through your browser settings:</p>
        <ul>
          <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
          <li><strong>Firefox:</strong> Options → Privacy &amp; Security → Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
          <li><strong>Edge:</strong> Settings → Privacy, Search, and Services → Cookies</li>
        </ul>
        <p>Please note that disabling certain cookies may affect the functionality of our website.</p>
      </UtilitySection>
    </UtilityPageLayout>
    </>
  );
};

export default CookiePolicy;
